import { AddColumnBodyCell } from "@/features/data-curation/components/AddColumnCells";
import { NUMBER_CELL, ROW_CELL, VALUE_CELL } from "@/features/data-curation/gridCells";
import type { CurationColumn } from "@/features/data-curation/types";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

const elapsedLabel = (startedAt: string, now: number) => {
  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) return null;
  const seconds = Math.max(0, Math.floor((now - started) / 1_000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

/** "Running for 2m 5s. ", updated each second; only this text re-renders. */
const RunningFor = ({ startedAt }: { startedAt: string }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const elapsed = elapsedLabel(startedAt, now);
  return elapsed ? <>Running for {elapsed}. </> : null;
};

const PLACEHOLDER_ROWS = 3;
const BAR_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-5/6", "w-2/5"];

const NoteRow = ({
  span,
  addColumnCell,
  children,
}: {
  span: number;
  addColumnCell: boolean;
  children: ReactNode;
}) => (
  <tr>
    <td colSpan={span} className="border-b border-zinc-200 bg-white p-0">
      <div
        className={`sticky left-0 w-full px-4 py-5 text-left ${
          addColumnCell ? "max-w-[min(36rem,100cqw_-_2.25rem)]" : "max-w-[min(36rem,100cqw)]"
        }`}
      >
        {children}
      </div>
    </td>
    {addColumnCell && <AddColumnBodyCell />}
  </tr>
);

const PlaceholderRow = ({
  columns,
  index,
  addColumnCell,
}: {
  columns: CurationColumn[];
  index: number;
  addColumnCell: boolean;
}) => (
  <tr aria-hidden data-placeholder-row>
    <td className={`${NUMBER_CELL} z-10 bg-white px-1 py-2 align-top`} />
    <td className={`${ROW_CELL} z-10 bg-white px-2 py-2 align-top`}>
      <span className="block h-2.5 w-28 rounded-full bg-zinc-200 motion-safe:animate-pulse" />
      <span className="mt-2 block h-2 w-12 rounded-full bg-zinc-200 motion-safe:animate-pulse" />
    </td>
    {columns.map((column, columnIndex) => (
      <td
        key={column.column_id}
        className={`${VALUE_CELL} border-zinc-200 px-2 py-2 align-top`}
      >
        <span className="flex items-center gap-2">
          <LoaderCircle
            className="h-3.5 w-3.5 shrink-0 text-zinc-300 motion-safe:animate-spin"
            aria-hidden
          />
          <span
            className={`h-2.5 rounded-full bg-zinc-200 motion-safe:animate-pulse ${
              BAR_WIDTHS[(index + columnIndex) % BAR_WIDTHS.length]
            }`}
            style={{ animationDelay: `${(index * 3 + columnIndex) * 150}ms` }}
          />
        </span>
      </td>
    ))}
    {addColumnCell && <AddColumnBodyCell />}
  </tr>
);

interface EmptyGridRowsProps {
  columns: CurationColumn[];
  collecting: boolean;
  filtered: boolean;
  collectionSummary: string | null;
  collectionStartedAt: string | null;
  /** The grid ends with the Add column button. */
  addColumnCell?: boolean;
}

export const EmptyGridRows = ({
  columns,
  collecting,
  filtered,
  collectionSummary,
  collectionStartedAt,
  addColumnCell = false,
}: EmptyGridRowsProps) => {
  const span = columns.length + 2;
  if (!collecting || filtered) {
    return (
      <NoteRow span={span} addColumnCell={addColumnCell}>
        <p className="text-sm font-medium text-zinc-700">
          {filtered ? "No rows match these filters" : "No rows submitted yet"}
        </p>
        <p className="mt-1 text-xs leading-5 text-zinc-500">
          {filtered
            ? "Clear or change the filters to inspect other rows."
            : "Rows appear after the agent submits its first sources."}
        </p>
      </NoteRow>
    );
  }
  return (
    <>
      <NoteRow span={span} addColumnCell={addColumnCell}>
        <div className="flex items-start gap-3" role="status" aria-live="polite">
          <LoaderCircle
            className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400 motion-safe:animate-spin"
            aria-hidden
          />
          <div className="min-w-0">
            <p className="text-sm font-medium text-zinc-700">
              The agent is collecting source data
            </p>
            <p className="mt-1 text-sm text-zinc-500">
              {collectionSummary ?? "Finding and reading the first sources."}
            </p>
            <p className="mt-2 text-xs leading-5 text-zinc-400">
              {collectionStartedAt && <RunningFor startedAt={collectionStartedAt} />}
              Rows appear here as each batch of sources is read and checked.
              Large cohorts can take tens of minutes.
            </p>
          </div>
        </div>
      </NoteRow>
      {Array.from({ length: PLACEHOLDER_ROWS }, (_, index) => (
        <PlaceholderRow
          key={index}
          columns={columns}
          index={index}
          addColumnCell={addColumnCell}
        />
      ))}
    </>
  );
};
